import { startTransition, useDeferredValue, useEffect, useMemo, useState } from "react"
import Icon from "../../Icon"
import api from "../../../utils/api"
import { formatMoney } from "../../../utils/format"
import { getUtilityDebtSummary, getUtilityWalletBalance } from "../../../pages/utbills/utilitySettlement"

const IMPERSONATE_DESTINATION = "/app/ut-bills"

const formatCount = (value) => (Number.isFinite(Number(value)) ? String(Number(value)) : "--")

const matchesProviderSearch = (provider, query) => {
  const search = String(query || "").trim().toLowerCase()
  if (!search) return true
  const customers = Array.isArray(provider?.customers) ? provider.customers : []
  const parts = [
    provider?.name,
    provider?.location,
    provider?.serviceOffered,
    provider?.ownerName,
    provider?.telephone,
    ...customers.flatMap((customer) => [customer?.name, customer?.phone, customer?.email]),
  ]
  return parts.some((value) => String(value || "").toLowerCase().includes(search))
}

function AdminUtilitiesModuleSection({ onImpersonateUser }) {
  const [collapsed, setCollapsed] = useState(true)
  const [walletsCollapsed, setWalletsCollapsed] = useState(true)

  const [providerCollapsedById, setProviderCollapsedById] = useState({})
  const [customerShowCountById, setCustomerShowCountById] = useState({})
  const [providersShowCount, setProvidersShowCount] = useState(10)
  const [walletsShowCount, setWalletsShowCount] = useState(10)

  const [searchInput, setSearchInput] = useState("")
  const deferredSearch = useDeferredValue(searchInput)

  const [summary, setSummary] = useState(null)
  const [summaryLoaded, setSummaryLoaded] = useState(false)
  const [summaryLoading, setSummaryLoading] = useState(false)
  const [summaryError, setSummaryError] = useState("")

  const [providers, setProviders] = useState([])
  const [wallets, setWallets] = useState([])
  const [detailsLoaded, setDetailsLoaded] = useState(false)
  const [detailsLoading, setDetailsLoading] = useState(false)
  const [detailsError, setDetailsError] = useState("")

  const loadSummary = async () => {
    try {
      setSummaryLoading(true)
      setSummaryError("")
      const res = await api.get("/ut-bills/admin/summary")
      setSummary(res?.data?.summary || null)
      setSummaryLoaded(true)
    } catch (error) {
      console.error("Failed to load admin utilities summary", error)
      setSummaryError(error?.response?.data?.error || "Failed to load utilities summary.")
      setSummaryLoaded(true)
    } finally {
      setSummaryLoading(false)
    }
  }

  const loadDetails = async () => {
    try {
      setDetailsLoading(true)
      setDetailsError("")
      const res = await api.get("/ut-bills/admin/all")
      setProviders(Array.isArray(res?.data?.providers) ? res.data.providers : [])
      setWallets(Array.isArray(res?.data?.wallets) ? res.data.wallets : [])
      setDetailsLoaded(true)
    } catch (error) {
      console.error("Failed to load admin utilities details", error)
      setDetailsError(error?.response?.data?.error || "Failed to load utilities details.")
      setDetailsLoaded(true)
    } finally {
      setDetailsLoading(false)
    }
  }

  useEffect(() => {
    if (!collapsed && !summaryLoaded && !summaryLoading) loadSummary()
  }, [collapsed, summaryLoaded, summaryLoading])

  useEffect(() => {
    if (!collapsed && !detailsLoaded && !detailsLoading) loadDetails()
  }, [collapsed, detailsLoaded, detailsLoading])

  useEffect(() => {
    setProvidersShowCount(10)
  }, [deferredSearch])

  const filteredProviders = useMemo(() => {
    if (!detailsLoaded) return []
    return providers.filter((provider) => matchesProviderSearch(provider, deferredSearch))
  }, [providers, detailsLoaded, deferredSearch])

  const handleRefresh = async () => {
    await Promise.all([loadSummary(), loadDetails()])
  }

  const toggleProvider = (id) => {
    setProviderCollapsedById((prev) => ({ ...prev, [id]: !(prev[id] ?? true) }))
  }

  const providersCountText = detailsLoaded ? formatCount(filteredProviders.length) : formatCount(summary?.totalProviders)
  const customersCountText = formatCount(summary?.totalCustomers)
  const outstandingText = formatMoney(getUtilityDebtSummary(summary || {}).amountDue)
  const collectedMonthText = formatMoney(summary?.collectedThisMonth || 0)
  const waivedText = formatMoney(summary?.totalWaived || 0)
  const walletsText = formatMoney(summary?.walletsBalance || 0)

  const renderCustomer = (customer) => (
    <div
      key={customer.id}
      className="flex items-center gap-2 py-1.5 pl-2 pr-1 border-l-2 border-gray-200 hover:border-emerald-300 hover:bg-emerald-50/30 transition-colors"
    >
      <Icon name="person" size="0.75rem" className="text-gray-300 flex-shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <p className="text-[11px] font-medium text-gray-700 truncate">{customer.name}</p>
          {customer.autoInvoiceEnabled === true && (
            <span className="flex-shrink-0 inline-flex items-center px-1 py-0 rounded text-[8px] font-semibold bg-emerald-50 text-emerald-600 border border-emerald-100">
              AUTO
            </span>
          )}
          {customer.status === "removed" && (
            <span className="flex-shrink-0 inline-flex items-center px-1 py-0 rounded text-[8px] font-semibold bg-gray-100 text-gray-400 border border-gray-200">
              REMOVED
            </span>
          )}
        </div>
        <p className="text-[9px] text-gray-400 truncate">{customer.phone || customer.email || "-"}</p>
      </div>
      <p className={`text-[11px] font-semibold flex-shrink-0 ${getUtilityDebtSummary(customer).amountDue > 0 ? "text-red-600" : "text-gray-400"}`}>
        {formatMoney(getUtilityDebtSummary(customer).amountDue)}
      </p>
      {onImpersonateUser && (
        <button
          onClick={() => onImpersonateUser(customer.id, { destination: IMPERSONATE_DESTINATION })}
          className="flex-shrink-0 text-[9px] uppercase font-semibold text-emerald-600 hover:text-emerald-800 hover:underline px-1"
          title="Log in as this customer"
        >
          Log in
        </button>
      )}
    </div>
  )

  const renderProvider = (provider) => {
    const isCollapsed = providerCollapsedById[provider.id] ?? true
    const showCount = Number(customerShowCountById[provider.id] || 5)
    const customers = Array.isArray(provider.customers) ? provider.customers : []
    const visibleCustomers = customers.slice(0, showCount)

    const accent = provider.isBridge
      ? { bg: "bg-sky-600", ring: "shadow-sky-100", badge: "bg-sky-50 text-sky-700 border-sky-100", bar: "border-l-sky-400" }
      : { bg: "bg-violet-600", ring: "shadow-violet-100", badge: "bg-violet-50 text-violet-700 border-violet-100", bar: "border-l-violet-400" }

    return (
      <div key={provider.id} className={`bg-white border-l-4 ${accent.bar}`}>
        <button
          onClick={() => toggleProvider(provider.id)}
          className="w-full flex items-center gap-3 p-3 text-left hover:bg-gray-50 transition-colors"
        >
          <div className={`w-8 h-8 sm:w-9 sm:h-9 flex-shrink-0 rounded-lg flex items-center justify-center shadow-sm ${accent.bg} ${accent.ring}`}>
            <Icon name="building" size="1rem" className="text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h6 className="text-sm font-bold text-gray-900 truncate">{provider.name}</h6>
              <span className={`flex-shrink-0 inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-semibold border ${accent.badge}`}>
                {provider.isBridge ? "From Rentals" : "Standalone"}
              </span>
              {provider.locked && (
                <span className="flex-shrink-0 inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-red-50 text-red-600 border border-red-100">
                  Blocked
                </span>
              )}
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] text-gray-500">
              <span>Owner: {provider.ownerName || "Unknown"}</span>
              {provider.location && (
                <>
                  <span className="text-gray-300">/</span>
                  <span className="truncate">{provider.location}</span>
                </>
              )}
              {provider.serviceOffered && (
                <>
                  <span className="text-gray-300">/</span>
                  <span className="truncate">{provider.serviceOffered}</span>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3 sm:gap-4 flex-shrink-0">
            <div className="text-right hidden sm:block">
              <p className="text-[8px] uppercase font-semibold text-gray-400 tracking-wider">Customers</p>
              <p className="text-xs font-semibold text-gray-900">{provider.customersCount}</p>
            </div>
            <div className="text-right">
              <p className="text-[8px] uppercase font-semibold text-gray-400 tracking-wider">Owed</p>
              <p className={`text-sm font-bold ${getUtilityDebtSummary(provider.totals || {}).amountDue > 0 ? "text-red-600" : "text-gray-900"}`}>
                {formatMoney(getUtilityDebtSummary(provider.totals || {}).amountDue)}
              </p>
            </div>
            <Icon name={isCollapsed ? "chevron-right" : "chevron-down"} size="0.875rem" className="text-gray-300" />
          </div>
        </button>

        {!isCollapsed && (
          <div className="border-t border-gray-100 bg-gray-50/50 p-3 space-y-3">
            {/* Provider money summary */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div className="bg-white p-2 rounded-lg border border-gray-100">
                <p className="text-[8px] uppercase font-bold text-gray-400 tracking-wider">Billed</p>
                <p className="text-xs font-bold text-gray-900">{formatMoney(provider.totals?.billed || 0)}</p>
              </div>
              <div className="bg-white p-2 rounded-lg border border-gray-100">
                <p className="text-[8px] uppercase font-bold text-gray-400 tracking-wider">Collected</p>
                <p className="text-xs font-bold text-emerald-700">{formatMoney(provider.totals?.collected || 0)}</p>
              </div>
              <div className="bg-white p-2 rounded-lg border border-gray-100">
                <p className="text-[8px] uppercase font-bold text-gray-400 tracking-wider">Waived</p>
                <p className="text-xs font-bold text-amber-600">{formatMoney(provider.totals?.waived || 0)}</p>
              </div>
              <div className="bg-white p-2 rounded-lg border border-gray-100">
                <p className="text-[8px] uppercase font-bold text-gray-400 tracking-wider">Expenses</p>
                <p className="text-xs font-bold text-gray-900">{formatMoney(provider.totals?.expenses || 0)}</p>
              </div>
            </div>

            {/* Bill types */}
            {Array.isArray(provider.categories) && provider.categories.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {provider.categories.map((category) => (
                  <span
                    key={category.id}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-white text-gray-600 border border-gray-200"
                  >
                    {category.name}
                    {category.autoInvoiceEnabled && (
                      <span className="text-emerald-600 font-semibold">auto</span>
                    )}
                  </span>
                ))}
              </div>
            )}

            {/* Provider actions */}
            {onImpersonateUser && provider.ownerId && (
              <button
                onClick={() => onImpersonateUser(provider.ownerId, { destination: IMPERSONATE_DESTINATION })}
                className="text-[11px] bg-emerald-600 text-white px-2.5 py-1 rounded hover:bg-emerald-700"
              >
                Log in as owner
              </button>
            )}

            {/* Customers */}
            <div>
              <p className="text-[10px] uppercase font-semibold text-gray-400 tracking-wider mb-1.5">
                Customers ({customers.length})
              </p>
              {customers.length === 0 ? (
                <p className="text-xs text-gray-400">No customers yet.</p>
              ) : (
                <div className="bg-white rounded-lg border border-gray-100 pl-1 pr-2 py-1 divide-y divide-gray-50">
                  {visibleCustomers.map(renderCustomer)}
                  {customers.length > showCount && (
                    <button
                      onClick={() =>
                        setCustomerShowCountById((prev) => ({ ...prev, [provider.id]: showCount + 10 }))
                      }
                      className="w-full text-left text-[11px] text-emerald-700 font-medium hover:underline pt-1.5"
                    >
                      Show more ({customers.length - showCount})
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    )
  }

  const visibleProviders = filteredProviders.slice(0, providersShowCount)
  const visibleWallets = wallets.slice(0, walletsShowCount)

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
      <button
        onClick={() => setCollapsed((prev) => !prev)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 bg-emerald-600 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-100">
            <Icon name="droplet-half" size="1.25rem" className="text-white" />
          </div>
          <div>
            <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-tight">Utilities Management</h4>
            <p className="text-[10px] text-gray-500 font-medium uppercase tracking-wider">Providers / Customers / Billing</p>
          </div>
        </div>
        <Icon name={collapsed ? "chevron-right" : "chevron-down"} size="1rem" className="text-gray-300 transition-transform duration-200" />
      </button>

      {!collapsed && (
        <div className="border-t border-gray-100 divide-y divide-gray-100">
          {/* Stats + search */}
          <div className="p-4 bg-gray-50/30 space-y-4">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
              <div className="bg-white p-2 rounded-lg border border-gray-100 shadow-sm">
                <p className="text-[8px] text-gray-400 uppercase font-bold tracking-wider">Companies</p>
                <p className="text-xs font-bold text-gray-900">{providersCountText}</p>
              </div>
              <div className="bg-white p-2 rounded-lg border border-gray-100 shadow-sm">
                <p className="text-[8px] text-gray-400 uppercase font-bold tracking-wider">Customers</p>
                <p className="text-xs font-bold text-gray-900">{customersCountText}</p>
              </div>
              <div className="bg-white p-2 rounded-lg border border-gray-100 shadow-sm">
                <p className="text-[8px] text-gray-400 uppercase font-bold tracking-wider">Owed</p>
                <p className="text-xs font-bold text-red-600">{outstandingText}</p>
              </div>
              <div className="bg-white p-2 rounded-lg border border-gray-100 shadow-sm">
                <p className="text-[8px] text-gray-400 uppercase font-bold tracking-wider">Collected (mo)</p>
                <p className="text-xs font-bold text-emerald-700">{collectedMonthText}</p>
              </div>
              <div className="bg-white p-2 rounded-lg border border-gray-100 shadow-sm">
                <p className="text-[8px] text-gray-400 uppercase font-bold tracking-wider">Waived</p>
                <p className="text-xs font-bold text-amber-600">{waivedText}</p>
              </div>
              <div className="bg-white p-2 rounded-lg border border-gray-100 shadow-sm">
                <p className="text-[8px] text-gray-400 uppercase font-bold tracking-wider">Wallets</p>
                <p className="text-xs font-bold text-gray-900">{walletsText}</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                  <Icon name="search" size="0.875rem" className="text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search companies or customers..."
                  value={searchInput}
                  onChange={(event) => startTransition(() => setSearchInput(event.target.value))}
                  className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <button
                onClick={handleRefresh}
                disabled={summaryLoading || detailsLoading}
                className="flex-shrink-0 p-2 rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-60"
                title="Refresh"
              >
                <Icon name="arrow-clockwise" size="0.875rem" className={summaryLoading || detailsLoading ? "animate-spin" : ""} />
              </button>
            </div>

            {(summaryError || detailsError) && (
              <p className="text-xs text-red-600">{summaryError || detailsError}</p>
            )}
          </div>

          {/* Providers list */}
          <div>
            {detailsLoading && !detailsLoaded ? (
              <div className="p-4 text-xs text-gray-500">Loading utility companies...</div>
            ) : filteredProviders.length === 0 ? (
              <div className="p-4 text-xs text-gray-400">
                {deferredSearch ? "No matches." : "No utility companies yet."}
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {visibleProviders.map(renderProvider)}
                {filteredProviders.length > providersShowCount && (
                  <div className="p-3">
                    <button
                      onClick={() => setProvidersShowCount((count) => count + 10)}
                      className="text-xs text-emerald-700 font-medium hover:underline"
                    >
                      Show more companies ({filteredProviders.length - providersShowCount})
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Landlord utility wallets */}
          <div>
            <button
              onClick={() => setWalletsCollapsed((prev) => !prev)}
              className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Icon name="wallet2" size="0.875rem" className="text-gray-700" />
                <span className="text-sm font-medium text-gray-900">Wallets</span>
                <span className="text-xs text-gray-500">({wallets.length})</span>
              </div>
              <Icon name={walletsCollapsed ? "chevron-right" : "chevron-down"} size="0.875rem" className="text-gray-300" />
            </button>
            {!walletsCollapsed && (
              <div className="border-t border-gray-100 p-3 space-y-1.5">
                {wallets.length === 0 ? (
                  <p className="text-xs text-gray-400">No utility wallets yet.</p>
                ) : (
                  <>
                    {visibleWallets.map((wallet) => (
                      <div
                        key={wallet.id}
                        className="flex items-center justify-between gap-2 p-2 rounded-md border border-gray-100 bg-white"
                      >
                        <div className="min-w-0">
                          <p className="text-xs font-semibold text-gray-900 truncate">{wallet.propertyName}</p>
                          <p className="text-[10px] text-gray-500 truncate">{wallet.landlordName}</p>
                        </div>
                        <p className={`text-xs font-semibold flex-shrink-0 ${getUtilityWalletBalance(wallet) < 0 ? "text-red-600" : "text-gray-900"}`}>
                          {formatMoney(getUtilityWalletBalance(wallet))}
                        </p>
                      </div>
                    ))}
                    {wallets.length > walletsShowCount && (
                      <button
                        onClick={() => setWalletsShowCount((count) => count + 10)}
                        className="text-[11px] text-emerald-700 font-medium hover:underline"
                      >
                        Show more ({wallets.length - walletsShowCount})
                      </button>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminUtilitiesModuleSection
